import { useMemo, useState } from 'preact/hooks';
import { PageBuilderProvider } from 'client/courses/components/page-builder/page-builder-context';
import { SalesPageState } from 'client/courses/components/page-builder/page-editor';
import { blockDefinitions } from 'client/cal/page-builder/block-definitions';
import { BlockWrapper } from 'client/cal/page-builder/components/block-wrapper';
import { blockStyle } from 'client/courses/components/page-builder/block-viewer';
import { isLight, autoColor } from 'shared/colors';
import * as HeroBlock from 'client/cal/page-builder/hero-block';
import * as AboutBlock from 'client/cal/page-builder/about-block';
import * as ServicesBlock from 'client/cal/page-builder/services-block';
import * as TestimonialsBlock from 'client/cal/page-builder/testimonials-block';
import * as CredentialsBlock from 'client/cal/page-builder/credentials-block';
import * as CTABlock from 'client/cal/page-builder/cta-block';
import { LoadedProps, RouteLoadProps } from '@components/router';
import { defCalRoute } from './common';
import { rpx } from 'client/lib/rpx-client';
import { useCurrentUser } from 'client/lib/auth';
import { BtnPrimary } from '@components/buttons';
import { ThemeStyles } from './page-builder/theme-styles';
import { PageTheme } from 'server/types';
import { CalendarPageContent } from 'server/types/cal-overrides';
import { defaultTheme } from './page-builder/calendar-page-builder';

type Props = LoadedProps<typeof load>;

type CalendarLandingPageProps = {
  content: CalendarPageContent | undefined;
  hostId?: string;
  isPublished?: boolean;
  theme?: PageTheme;
};

export const route = defCalRoute({ load, Page, isPublic: true });

const blockViewerRegistry = {
  [HeroBlock.type]: HeroBlock.Viewer,
  [AboutBlock.type]: AboutBlock.Viewer,
  [ServicesBlock.type]: ServicesBlock.Viewer,
  [TestimonialsBlock.type]: TestimonialsBlock.Viewer,
  [CredentialsBlock.type]: CredentialsBlock.Viewer,
  [CTABlock.type]: CTABlock.Viewer,
} as const;

async function load(props: RouteLoadProps) {
  const { hostId } = props.params;
  const result = await rpx.ruzcal.getPublicCalendarPage({ hostId });

  return {
    content: result.content,
    hostId: hostId as string | undefined,
    isPublished: result.isPublished,
    theme: result.content?.theme || defaultTheme,
  };
}

function Page({ state }: Props) {
  return <CalendarLandingPage {...state} />;
}

export function CalendarLandingPage({
  content,
  hostId,
  isPublished = true,
  theme = defaultTheme,
}: CalendarLandingPageProps) {
  const user = useCurrentUser();
  const [pageState, setPageState] = useState<SalesPageState>({
    content: content || { blockIds: [], blocks: {} },
    selectedId: '',
    selection: { id: '', value: { start: [0], end: [0] } },
  });
  const isPreview = !isPublished && user && hostId === user.id;

  if (!content || (!isPublished && !isPreview)) {
    return (
      <div class="flex items-center justify-center min-h-screen bg-slate-900 text-slate-400">
        <div class="text-center">
          <h1 class="text-2xl font-semibold mb-2">Coming soon...</h1>
          <p>This page is being prepared.</p>
        </div>
      </div>
    );
  }

  const { blockIds, blocks } = content;

  return (
    <PageBuilderProvider
      pageState={pageState}
      setPageState={setPageState}
      blockDefinitions={blockDefinitions}
    >
      <ThemeStyles theme={theme} />
      {isPreview && (
        <header class="flex justify-center fixed top-4 inset-x-0 z-50">
          <nav class="bg-gradient-to-r from-violet-500 to-pink-500 via-sky-600 text-white p-2 pl-4 font-semibold rounded-full flex items-center gap-4 shadow-xl">
            This is a preview of your landing page.
            <BtnPrimary
              href={`/calendar/page-builder`}
              class="rounded-full inline-flex gap-2 px-4 py-2 bg-white text-gray-700 hover:shadow-xl hover:-translate-0.5"
            >
              Edit
            </BtnPrimary>
          </nav>
        </header>
      )}
      <div class="sales-page @container" data-theme-styles>
        {blockIds?.map((blockId) => {
          const block = blocks?.[blockId];
          if (!block) {
            return undefined;
          }

          return <LandingPageBlock key={blockId} block={block} />;
        })}
      </div>
    </PageBuilderProvider>
  );
}

function LandingPageBlock({ block }: { block: any }) {
  const elId = `block-${block.id}`;
  const bgcolor = block.bgcolor || '#ffffff';
  const isBgLight = useMemo(() => isLight(bgcolor), [bgcolor]);
  const fgcolor = block.fgcolor || autoColor(bgcolor);
  const style = blockStyle(block, fgcolor);
  const Viewer = blockViewerRegistry[block.type as keyof typeof blockViewerRegistry];

  return (
    <div
      id={elId}
      key={block.id}
      style={style}
      data-type={block.type}
      class={`relative ${isBgLight ? '' : 'sales-page-dark'}`}
    >
      <BlockWrapper paddingt={block.paddingt} paddingb={block.paddingb}>
        {Viewer && (
          <Viewer
            id={elId}
            state={block.state}
            bgcolor={bgcolor}
            fgcolor={fgcolor}
            isBgLight={isBgLight}
          />
        )}
        {!Viewer && (
          <div class="p-4 bg-yellow-100 text-yellow-800 rounded">
            Unknown block type: {block.type}
          </div>
        )}
      </BlockWrapper>
    </div>
  );
}
